// Chatbot History detail page — tabbed view with two aggregations:
//   - By user:    sum of messages across in-dashboard + embed history per owner.
//   - By chatbot: messages per embedded chatbot (chatspace_history only,
//                 since chat_message_history doesn't track chatbot_id).
// Both sourced from /admin/dashboard/chatbot-history?by=user|chatbot.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
    TableContainer, Paper, Pagination, CircularProgress, Alert, Stack,
    IconButton, Tooltip, Button, Tabs, Tab,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import axiosInstance from '../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const PAGE_SIZE = 25;

const ChatbotHistory = () => {
    const navigate = useNavigate();
    const [tab, setTab] = useState('user');  // 'user' | 'chatbot'

    const [data, setData] = useState({ items: [], total: 0, pages: 1, total_messages: 0 });
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/admin/dashboard/chatbot-history', {
                params: { by: tab, page, limit: PAGE_SIZE },
            });
            setData({
                items: res.data?.items || [],
                total: res.data?.total || 0,
                pages: res.data?.pages || 1,
                total_messages: res.data?.total_messages || 0,
            });
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load chat history.');
        } finally {
            setLoading(false);
        }
    }, [tab, page]);

    useEffect(() => { load(); }, [load]);

    const handleTab = (_, val) => {
        setPage(1);
        setTab(val);
    };

    return (
        <Box sx={{ fontFamily }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')}
                    sx={{ fontFamily, textTransform: 'none', mb: 2 }}>
                Back to dashboard
            </Button>

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                   alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1} sx={{ mb: 1.5 }}>
                <Box>
                    <Typography sx={{ fontFamily, fontSize: '1.4rem', fontWeight: 800, color: '#0f172a' }}>
                        Chatbot History
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                        Message volume across the platform. Switch the tab to slice by user or by chatbot.
                    </Typography>
                </Box>
                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8' }}>
                    {data.total_messages} total messages
                </Typography>
            </Stack>

            <Tabs
                value={tab} onChange={handleTab}
                sx={{ mb: 2, '& .MuiTab-root': { fontFamily, textTransform: 'none', fontWeight: 600 } }}
            >
                <Tab label="By user" value="user" />
                <Tab label="By chatbot" value="chatbot" />
            </Tabs>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error" sx={{ fontFamily }}>{error}</Alert>
            ) : data.items.length === 0 ? (
                <Typography sx={{ fontFamily, color: '#64748b', py: 6, textAlign: 'center' }}>
                    No data for this slice.
                </Typography>
            ) : (
                <>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px' }}>
                        <Table aria-label={`chat-history-${tab}`}>
                            <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                                <TableRow>
                                    {tab === 'user' ? (
                                        <>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }}>User ID</TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }}>Email</TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }} align="right">Messages</TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }} align="right">Actions</TableCell>
                                        </>
                                    ) : (
                                        <>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }}>Chatbot ID</TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }}>Chatbot name</TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }}>Owner</TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }} align="right">Messages</TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }} align="right">Actions</TableCell>
                                        </>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.items.map((row, i) => (
                                    <TableRow key={i} hover>
                                        {tab === 'user' ? (
                                            <>
                                                <TableCell sx={{ fontFamily, fontWeight: 600 }}>#{row.user_id}</TableCell>
                                                <TableCell sx={{ fontFamily }}>{row.user_email}</TableCell>
                                                <TableCell sx={{ fontFamily, fontWeight: 700, color: '#10b981' }} align="right">
                                                    {row.message_count}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="View user">
                                                        <IconButton size="small"
                                                                    onClick={() => navigate(`/users/${row.user_id}`)}>
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </>
                                        ) : (
                                            <>
                                                <TableCell sx={{ fontFamily, fontWeight: 600 }}>#{row.chatbot_id}</TableCell>
                                                <TableCell sx={{ fontFamily }}>{row.chatbot_name}</TableCell>
                                                <TableCell sx={{ fontFamily, color: '#64748b' }}>
                                                    {row.team_owner_id ? `#${row.team_owner_id}` : '--'}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontWeight: 700, color: '#10b981' }} align="right">
                                                    {row.message_count}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row.team_owner_id ? (
                                                        <Tooltip title="View owner">
                                                            <IconButton size="small"
                                                                        onClick={() => navigate(`/users/${row.team_owner_id}`)}>
                                                                <VisibilityIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : null}
                                                </TableCell>
                                            </>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {data.pages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Pagination
                                count={data.pages} page={page}
                                onChange={(_, p) => setPage(p)}
                                color="primary" size="small"
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default ChatbotHistory;
